import React, { useState, useEffect } from 'react';
import axios from '../../axios/axiosconf';
import {
    OPCIONES_TIPO_USUARIO_SISPRO,
    TIPO_USUARIO_SISPRO_DEFAULT,
} from './opcionesTipoUsuarioSispro';

/**
 * Modal para completar credenciales SISPRO del profesional del RIPS (estado = 0).
 */
function ModalConfigurarSispro({ show, onClose, ripsId, ripsUsersId, onGuardado }) {
    const [nombre, setNombre] = useState('');
    const [tipoDocumento, setTipoDocumento] = useState('CC');
    const [identificacion, setIdentificacion] = useState('');
    const [password, setPassword] = useState('');
    const [nit, setNit] = useState('');
    const [tipoUsuario, setTipoUsuario] = useState(TIPO_USUARIO_SISPRO_DEFAULT);
    const [usersId, setUsersId] = useState('');
    const [usuarios, setUsuarios] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [errorlist, setErrorlist] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!show || !ripsId) return;

        const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
        if (userdata.user_id) {
            axios.get('/usuarios?uid=' + userdata.user_id)
                .then((res) => setUsuarios(res.data || []))
                .catch(() => setUsuarios([]));
        }

        // Siempre limpiar el password al abrir (nunca cargar del backend)
        setPassword('');

        axios.post('/rips-sispro/datos-configuracion', { rips_id: ripsId })
            .then((res) => {
                const d = res.data || {};
                if (d.nombre) setNombre(d.nombre);
                if (d.tipo_documento) setTipoDocumento(d.tipo_documento);
                if (d.identificacion) setIdentificacion(d.identificacion);
                if (d.nit) setNit(d.nit);
                if (d.tipo_usuario) setTipoUsuario(d.tipo_usuario);
                const uid = d.users_id || ripsUsersId || '';
                setUsersId(uid ? String(uid) : '');
                // Contraseña siempre vacía - el usuario debe ingresarla
                setPassword('');
            })
            .catch(() => {
                if (ripsUsersId) setUsersId(String(ripsUsersId));
            });
    }, [show, ripsId, ripsUsersId]);

    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorlist({});
        setGuardando(true);

        const payload = {
            rips_id: ripsId,
            nombre,
            tipo_documento: tipoDocumento,
            identificacion,
            password,
            nit: nit || identificacion,
            tipo_usuario: tipoUsuario,
        };

        if (usersId) {
            payload.users_id = parseInt(usersId, 10);
        } else if (ripsUsersId) {
            payload.users_id = parseInt(ripsUsersId, 10);
        }

        try {
            await axios.post('/rips-sispro/configurar-usuario', payload);
            if (onGuardado) {
                await onGuardado();
            }
            onClose();
        } catch (error) {
            if (error.response?.status === 422 && error.response.data?.errors) {
                setErrorlist(error.response.data.errors);
            } else {
                setErrorlist({
                    general: [error.response?.data?.message || 'No se pudo guardar la configuración SISPRO'],
                });
            }
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div
            className="modal fade show lf-ds-modal"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10070 }}
            tabIndex="-1"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header lf-ds-modal-head">
                        <h5 className="modal-title mb-0 font-weight-bold">
                            <i className="fas fa-user-cog mr-2 text-info"></i>
                            Configurar usuario SISPRO
                        </h5>
                        <button type="button" className="close" onClick={onClose} aria-label="Cerrar">
                            <span>&times;</span>
                        </button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p className="text-muted small">
                                Complete las credenciales del profesional para enviar este RIPS a SISPRO.
                                La contraseña se guarda en el sistema para autenticación con el ministerio.
                            </p>

                            {errorlist.general && (
                                <div className="alert alert-danger py-2">{errorlist.general[0]}</div>
                            )}

                            <div className="form-group">
                                <label>Nombre descriptivo *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    required
                                />
                                {errorlist.nombre && <small className="text-danger">{errorlist.nombre[0]}</small>}
                            </div>

                            <div className="row">
                                <div className="col-md-6 form-group">
                                    <label>Tipo documento *</label>
                                    <select
                                        className="form-control"
                                        value={tipoDocumento}
                                        onChange={(e) => setTipoDocumento(e.target.value)}
                                        required
                                    >
                                        <option value="CC">CC</option>
                                        <option value="CE">CE</option>
                                        <option value="NIT">NIT</option>
                                        <option value="TI">TI</option>
                                        <option value="PA">PA</option>
                                    </select>
                                </div>
                                <div className="col-md-6 form-group">
                                    <label>Identificación *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={identificacion}
                                        onChange={(e) => setIdentificacion(e.target.value)}
                                        required
                                    />
                                    {errorlist.identificacion && (
                                        <small className="text-danger">{errorlist.identificacion[0]}</small>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Tipo de usuario SISPRO *</label>
                                <select
                                    className="form-control"
                                    value={tipoUsuario}
                                    onChange={(e) => setTipoUsuario(e.target.value)}
                                    required
                                >
                                    {OPCIONES_TIPO_USUARIO_SISPRO.map((op) => (
                                        <option key={op.value} value={op.value}>{op.label}</option>
                                    ))}
                                </select>
                                {errorlist.tipo_usuario && (
                                    <small className="text-danger">{errorlist.tipo_usuario[0]}</small>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Contraseña SISPRO *</label>
                                <div className="input-group">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                    />
                                    <div className="input-group-append">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                    </div>
                                </div>
                                {errorlist.password && (
                                    <small className="text-danger">{errorlist.password[0]}</small>
                                )}
                            </div>

                            <div className="form-group">
                                <label>NIT</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={nit}
                                    onChange={(e) => setNit(e.target.value)}
                                    placeholder="Si vacío, se usa la identificación"
                                />
                            </div>

                            <div className="form-group">
                                <label>Profesional asociado</label>
                                <select
                                    className="form-control"
                                    value={usersId}
                                    onChange={(e) => setUsersId(e.target.value)}
                                >
                                    <option value="">— Sin asociar —</option>
                                    {usuarios.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name} {u.lastname}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer border-top bg-light">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={guardando}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={guardando}>
                                {guardando ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin mr-1"></i> Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save mr-1"></i> Guardar y continuar
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ModalConfigurarSispro;
